<template>
  <div class="flex flex-col gap-2 pt-3 border-t border-[#f1f5f9] sm:flex-row sm:items-center sm:justify-between text-xs">
    <p class="text-[#64748b] font-medium">
      {{ startItem }}-{{ endItem }} of {{ totalItems }} clients
    </p>

    <div
      v-if="totalPages > 1"
      class="flex items-center gap-1"
    >
      <button
        type="button"
        class="page-nav-btn"
        :disabled="currentPage === 1"
        title="Previous page"
        @click="$emit('update:current-page', currentPage - 1)"
      >
        <ChevronLeft class="h-3.5 w-3.5" />
      </button>

      <template
        v-for="(item, idx) in visiblePages"
        :key="idx"
      >
        <span
          v-if="item === '...'"
          class="px-1 text-[#94a3b8]"
        >
          ...
        </span>
        <button
          v-else
          type="button"
          class="page-num-btn"
          :class="item === currentPage ? 'page-num-btn--active' : ''"
          @click="$emit('update:current-page', Number(item))"
        >
          {{ item }}
        </button>
      </template>

      <button
        type="button"
        class="page-nav-btn"
        :disabled="currentPage === totalPages"
        title="Next page"
        @click="$emit('update:current-page', currentPage + 1)"
      >
        <ChevronRight class="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";

const props = defineProps<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}>();

defineEmits<{
  (event: "update:current-page", value: number): void;
}>();

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.pageSize + 1;
});

const endItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return Math.min(props.currentPage * props.pageSize, props.totalItems);
});

const visiblePages = computed<(number | string)[]>(() => {
  const total = props.totalPages;
  const current = props.currentPage;

  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 3) {
    return [1, 2, 3, "...", total];
  }

  if (current >= total - 2) {
    return [1, "...", total - 2, total - 1, total];
  }

  return [1, "...", current, "...", total];
});
</script>

<style scoped>
.page-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 0.65rem;
  border: 1px solid #d7e2ef;
  background: #f6f9ff;
  color: #2855a6;
  transition: all 0.15s ease;
}

.page-nav-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  background: #eaf3fb;
}

.page-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-num-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.85rem;
  height: 1.85rem;
  padding: 0 0.4rem;
  border-radius: 0.65rem;
  font-weight: 800;
  font-size: 0.78rem;
  color: #64748b;
  transition: all 0.15s ease;
}

.page-num-btn:hover {
  background: #f6f9ff;
  color: #2855a6;
}

.page-num-btn--active {
  background: #eaf3fb;
  color: #2855a6;
  border: 1px solid #8eb4ff;
  font-weight: 800;
}
</style>
